import useUserDetails from "hooks/useUserDetails";
import { handleGetAllInvoice } from "api/fetchers";
import { AddIcon, ArrowDown, DocsFilledIcon, SearchIcon } from "asset/icons";
import { currencyToDisplay, formatDate } from "component/flex/utils";
import Input from "component/form/Input";
import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import { Bars } from "react-loader-spinner";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import { formatMoney } from "utilities/formatMoney";
import { CheckIcon } from "lucide-react";
import { MINIMUM_AMOUNT } from "constants/FinanceConstants";
import { useNavigate } from "react-router-dom";
import currencyPairCheck from "utilities/currencyPairCheck";

const PurchaseInvoiceSelect = ({ setOrderDimensionsList, forFinance = false , forRepayment }) => {
    const [openModal, setOpenModal] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const { currency } = useSelector((state) => state);
    const { businessId } = useUserDetails();
    const navigate = useNavigate();

    const { data: invoiceList, isLoading } = useQuery({
        queryKey: ["alInvoice", businessId],
        queryFn: () =>
            handleGetAllInvoice({
                businessId: businessId,
                currency,
                type: "outbound",
                period: "all",
                sortBy: "newest-first",
            }),
        enabled: !!businessId,
    });
    
    const confirmedList = invoiceList?.inbound;
    const confirmedInvoiceForFinance = invoiceList?.inbound?.filter(
        (order) =>
            (order?.paymentStatus === "UNPAID" || order?.paymentStatus === "DUE") &&
            order?.totalAmount >= MINIMUM_AMOUNT
    );
    const confirmedInvoiceForRepayment = invoiceList?.inbound?.filter(
        (order) =>
            (order?.paymentStatus === "UNPAID" || order?.paymentStatus === "DUE")  
    );
    const poToRender = forRepayment ? confirmedInvoiceForRepayment : forFinance ? confirmedInvoiceForFinance : confirmedList;

    const handleSelect = (id) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((selectedId) => selectedId !== id) : [...prev, id]
        );
    };

    const clearSelection = () => setSelectedIds([]);

    return (
        <>
            {forFinance ? (
                <p
                    onClick={() => setOpenModal(true)}
                    className="h-[56px] w-full bg-secondary-30 flex items-center justify-between rounded-md p-3 cursor-pointer"
                >
                    <span className="text-sm font-light">Select an invoice</span>

                    <ArrowDown />
                </p>
            ) : (
                <p
                    className="flex items-center !px-3.5 h-16 bg-white rounded-lg text-main-dark cursor-pointer"
                    onClick={() => setOpenModal(true)}
                >
                    <DocsFilledIcon className="!mr-2" />
                    Select Purchase Order
                    <AddIcon className="ml-auto" />
                </p>
            )}

            <Modal show={openModal} centered onHide={() => setOpenModal(false)}>
                <div
                    style={{ maxHeight: "85vh" }}
                    className="rounded-2xl pt-2 text-main-dark overflow-hidden bg-[#F5F4FA]"
                >
                    <div className="!px-6 my-2">
                        <Input
                            onChange={(value) => {
                                setSearchTerm(value);
                            }}
                            value={searchTerm}
                            placeholder="Search by item, email or customer"
                            unit={<SearchIcon className="-order-1 !ml-4 !size-6" />}
                            className="!shadow-[inset_0_0_0_1000px_#EAECF0] !pl-3 placeholder:text-base placeholder:font-normal"
                        />
                    </div>
                    {selectedIds.length > 0 && (
                        <div className="px-4 mt-1 mb-1 text-sm">
                            <div className="flex justify-between">
                                <p className="text-gray-400">"{selectedIds.length}" selected</p>
                                <button onClick={clearSelection} className="text-lg font-bold">
                                    Clear selection
                                </button>
                            </div>
                        </div>
                    )}
                    {/* Transactions List */}
                    {isLoading ? (
                        <Bars height="20" width="20" color="#fff" ariaLabel="bars-loading" />
                    ) : (
                        <div className="p-4 space-y-3 overflow-y-auto h-[50vh]">
                            {poToRender?.length ? (
                                poToRender?.map((transaction, index) => {
                                    const id = `${transaction?._id}`;
                                    const isSelected = selectedIds.includes(id);

                                    return (
                                        <div
                                            key={id}
                                            onClick={() => {
                                                return forFinance
                                                    ? setOrderDimensionsList(transaction)
                                                    : handleSelect(id);
                                            }}
                                            className="flex items-center justify-between px-2 py-2 bg-white rounded-lg"
                                        >
                                            <div className="flex items-center gap-2">
                                                <div className="relative size-5 group">
                                                    <div
                                                        style={{
                                                            border: isSelected
                                                                ? ""
                                                                : "1px solid #D0D5DD",
                                                            background: isSelected
                                                                ? "#6C47FF"
                                                                : "white",
                                                        }}
                                                        onClick={() =>
                                                            forFinance
                                                                ? setOrderDimensionsList(
                                                                      transaction
                                                                  )
                                                                : handleSelect(id)
                                                        }
                                                        className="flex items-center justify-center rounded-full size-5"
                                                    >
                                                        {isSelected && (
                                                            <CheckIcon size="14" color="white" />
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="space-y-1">
                                                    <div className="text-sm font-medium text-[#111827]">
                                                        {transaction?.title} •{" "}
                                                        {forFinance
                                                            ? transaction?.inbound?.businessName
                                                            : transaction?.outbound?.businessName}
                                                    </div>
                                                    <div className="flex gap-2 mt-0 text-sm">
                                                        <span className="text-[#22C55E] font-medium">
                                                            {transaction.orderStatus}
                                                        </span>
                                                        <span className="text-[#9CA3AF]">•</span>
                                                        <span className="text-[#6B7280]">
                                                            {formatDate(transaction.createdAt)}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <div className="text-sm font-medium text-[#111827]">
                                            {currencyToDisplay(currency,
                                              transaction?.totalAmount
                                                    )}
                                                </div>
                                                <div className="text-xs text-[#6B7280]">
                                                    {transaction?.orderItems?.length} Items
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            ) : forFinance ? (
                                <div className="flex flex-col items-center">
                                    <p className="px-3 py-2 font-light text-center text-gray-700">
                                        You don't have any outgoing invoices about{" "}
                                                {currencyPairCheck(currency)}{" "}3 million. <br />{" "}
                                    </p>
                                    <button
                                        onClick={() => {
                                            navigate("/invoice/create-invoice");
                                        }}
                                        className="!text-secondary text-center mx-auto"
                                    >
                                        Create one now.
                                    </button>
                                </div>
                            ) : (
                                <p className="px-3 py-2 font-light text-center text-gray-700">
                                    You don't have any invoice yet
                                </p>
                            )}
                        </div>
                    )}
                    <div className="flex justify-end gap-2 px-4 py-2 text-xs bg-white border-t rounded-sm border-t-gray-200">
                        <button
                            onClick={() => {
                                if (!forFinance) {
                                    setOpenModal(false);
                                    setOrderDimensionsList([]);
                                } else {
                                    setOpenModal(false);
                                }
                            }}
                            className="px-4 py-2 text-gray-700"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={() => {
                                if (!forFinance) {
                                    setOrderDimensionsList(() =>
                                        poToRender?.filter((item) =>
                                            selectedIds.includes(item?._id)
                                        )
                                    );
                                    setOpenModal(false);
                                } else {
                                    setOpenModal(false);
                                }
                            }}
                            className="bg-[#6F00FF] text-white px-3 py-1 rounded-md text-md"
                        >
                            Confirm
                        </button>
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default PurchaseInvoiceSelect;
